import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OrderRepositoryService } from 'src/app/checkout/services/order-repository.service';
import { AddToCart, Cart, CartApiService, CreateQuotation, UpdateCart } from './cart-api.service';

export interface ProductCartDta {
  id: string
  quantity: number,
}

@Injectable({
  providedIn: 'root'
})
export class CartRepositoryService {
  private cartApiService = inject(CartApiService);
  private orderRepositoryService = inject(OrderRepositoryService);

  // ===== SERVICE STATE MANAGEMENT =====
  private _isLoading = signal<boolean>(false);

  // ===== PUBLIC READONLY SIGNALS =====
  readonly isLoading = this._isLoading.asReadonly();

  // ===== CART STATE MANAGEMENT =====
  private _cart = signal<Cart | null>(null);
  readonly cart = this._cart.asReadonly();

  constructor() {
    this.initializeService().catch(error => {
      // Cart service auto-initialization failed
    });
  }


  private _addToCartAfterLogin = signal<ProductCartDta | null>(null);
  readonly addToCartAfterLogin = this._addToCartAfterLogin.asReadonly();

  private _buttonLoading = signal(false);
  readonly buttonLoading = this._buttonLoading.asReadonly();

  private _buyNowButtonLoading = signal(false);
  readonly buyNowButtonLoading = this._buyNowButtonLoading.asReadonly();

  private _pageMasking = signal(false);
  readonly pageMasking = this._pageMasking.asReadonly();

  // Removed: refresh-clearing signals and listeners not required in Angular 20




  // ===== SERVICE INITIALIZATION =====
  async initializeService(): Promise<void> {
    try {
      // Try to fetch initial cart data
      await this.fetchCart();
    } catch (error) {
      // Error during initialization
    }
  }


  getServiceStatus(): {
    isLoading: boolean;
  } {
    return {
      isLoading: this._isLoading()
    };
  }

  // ===== CART OPERATIONS =====
  async addProductToCart(reqObj: AddToCart): Promise<void> {

    try {
      this._isLoading.set(true);
      this._buttonLoading.set(true);

      const cart = await firstValueFrom(this.cartApiService.addProductToCart(reqObj));
      this.updateSavedCart(cart);
    } catch (error) {
      // Re-throw the error so it can be handled by the calling component
      throw error;
    } finally {
      this._isLoading.set(false);
      this._buttonLoading.set(false);
    }
  }

  addProductToCartAfterLogin(productData: ProductCartDta) {
    this._addToCartAfterLogin.set(productData);
  }



  async addProductToCartBuyNowFlow(reqObj: AddToCart): Promise<void> {

    try {
      this._isLoading.set(true);
      this._buyNowButtonLoading.set(true);

      const cart = await firstValueFrom(this.cartApiService.addProductToCart(reqObj));
      this.updateSavedCart(cart);
    } catch (error) {
      // Failed to add product to cart for buy now flow
    } finally {
      this._isLoading.set(false);
      this._buyNowButtonLoading.set(false);
    }
  }

  async updateCart(reqObj: UpdateCart): Promise<void> {
    try {
      this._isLoading.set(true);

      const currentCart = this._cart();

      if (!currentCart) {
        return;
      }

      reqObj = {
        ...reqObj,
        cartId: currentCart.id
      };

      const cart = await firstValueFrom(this.cartApiService.updateProductCart(reqObj));
      this.updateSavedCart(cart);
    } catch (error) {
      // Don't set service error - let component handle the error
      throw error; // Re-throw the error so component can catch it
    } finally {
      this._isLoading.set(false);
    }
  }

  async removeProductFromCart(cartItemId: string): Promise<void> {
    try {
      this._isLoading.set(true);

      const cart = await firstValueFrom(this.cartApiService.removeProductFromCart(cartItemId));
      this.updateSavedCart(cart);
    } catch (error) {
      // Re-throw the error so it can be handled by the calling component
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  async fetchCart(): Promise<void> {

    try {
      this._isLoading.set(true);

      const cart = await firstValueFrom(this.cartApiService.fetchCart());
      this.updateSavedCart(cart);
    } catch (error) {
      // Failed to fetch cart
      this.updateSavedCart(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  updateSavedCart(cart: Cart | null) {
    this._cart.set(cart);
  }

  clearCart() {
    this.updateSavedCart(null);
  }

  removeCartItems() {
    this.updateSavedCart(null);
  }

  loadCart(): void {
    this.fetchCart();
  }

  async initiateQuotationFromCart(cartId: string): Promise<CreateQuotation | null> {

    try {
      this._isLoading.set(true);
      this._buttonLoading.set(true);

      const response = await firstValueFrom(this.cartApiService.initiateQuotation(cartId));
      this.updateSavedCart(response?.cart);
      this.orderRepositoryService.setQuotation(response?.quotation);
      return response;
    } catch (error) {
      // Failed to initiate quotation from cart
      return null;
    } finally {
      this._isLoading.set(false);
      this._buttonLoading.set(false);
    }
  }


  // Public setter methods for signals
  setPageMasking(masking: boolean) {
    this._pageMasking.set(masking);
  }

  setButtonLoading(loading: boolean) {
    this._buttonLoading.set(loading);
  }

}
